/**
 * Backup Warning Component
 *
 * Shows backup reminder and download option
 */

import { Download, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/useToast";
import { useBrowserWallet } from "@nullaone/wallet-react";

export function BackupWarning() {
  const { t } = useTranslation();
  const { downloadBackup, markBackedUp, closeModal } = useBrowserWallet();
  const { error: showError } = useToast();

  const handleDownload = async () => {
    try {
      await downloadBackup();
    } catch (error) {
      console.error("Failed to download backup:", error);
      showError(t("wallet.backup.downloadFailed"));
      // No file on disk — keep the modal open so the user can retry.
      return;
    }
    try {
      await markBackedUp();
    } catch (error) {
      // The user already has the backup file; only the confirmation flag
      // failed. needsBackup stays true so the banner keeps nagging.
      console.error("markBackedUp failed:", error);
      showError(t("wallet.mnemonicDisplay.markBackedUpFailed"));
    }
    closeModal();
  };

  const handleSkip = () => {
    closeModal();
  };

  return (
    <div className="space-y-6 py-4">
      {/* Warning banner */}
      <div className="rounded-lg border border-warning/40 bg-warning/10 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-warning-dark mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-warning-dark">
              {t("wallet.backup.warningTitle")}
            </p>
            <p className="text-xs text-warning-dark/80">
              {t("wallet.backup.warningDesc")}
            </p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="space-y-4 text-sm text-muted-foreground">
        <p>{t("wallet.backup.infoIntro")}</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>{t("wallet.backup.info1")}</li>
          <li>{t("wallet.backup.info2")}</li>
          <li>{t("wallet.backup.info3")}</li>
        </ul>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <Button className="w-full gap-2" size="lg" onClick={handleDownload}>
          <Download className="h-4 w-4" />
          {t("wallet.backup.download")}
        </Button>

        <Button variant="ghost" className="w-full" onClick={handleSkip}>
          {t("wallet.backup.skip")}
        </Button>
      </div>

      {/* Reminder */}
      <p className="text-xs text-center text-muted-foreground">
        {t("wallet.backup.laterReminder")}
      </p>
    </div>
  );
}

/**
 * Inline backup warning banner for use in other components
 */
export function BackupWarningBanner() {
  const { t } = useTranslation();
  const { needsBackup, openModal } = useBrowserWallet();

  if (!needsBackup) return null;

  return (
    <div className="rounded-lg border border-warning/40 bg-warning/10 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning-dark" />
          <span className="text-sm text-warning-dark">
            {t("wallet.backup.bannerLabel")}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-warning-dark border-warning/40 hover:bg-warning/10"
          onClick={() => openModal("backup")}
        >
          {t("wallet.backup.bannerCta")}
        </Button>
      </div>
    </div>
  );
}
